import { ARCADE_GAME_KINDS, type ArcadeGameKind } from "./games";

// The daily challenge: one song and one game for everyone, picked from the UTC
// date so every visitor and the server agree without storing anything. Convex
// bundles this file (convex/arcadeScores.ts checks the game of a challenge
// score), so it only imports relative files with no browser code.

export const CHALLENGE_PARAM = "challenge";
export const DAY_MS = 24 * 60 * 60 * 1000;

const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function utcDateKey(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function isDateKey(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = DATE_KEY.exec(value);
  if (!match) return false;
  const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isFinite(ms) && utcDateKey(ms) === value;
}

export const dayIndexOf = (date: string): number => Math.floor(Date.parse(`${date}T00:00:00Z`) / DAY_MS);

export const nextChallengeAt = (ms: number): number => (Math.floor(ms / DAY_MS) + 1) * DAY_MS;

// FNV-1a, the same hash the song themes use.
export function hash32(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

// Games run in blocks of six days, each block a shuffled order of all six, so
// every game comes round once a block and never two days in a row.
function blockOrder(block: number): ArcadeGameKind[] {
  const order = [...ARCADE_GAME_KINDS];
  let seed = hash32(`challenge-block:${block}`);
  for (let i = order.length - 1; i > 0; i--) {
    seed = Math.imul(seed ^ (seed >>> 15), 0x2c1b3c6d) >>> 0;
    const j = seed % (i + 1);
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}

function orderForBlock(block: number): ArcadeGameKind[] {
  const order = blockOrder(block);
  const previous = blockOrder(block - 1);
  if (order[0] === previous[previous.length - 1]) [order[0], order[1]] = [order[1], order[0]];
  return order;
}

export function challengeGameFor(date: string): ArcadeGameKind {
  const day = dayIndexOf(date);
  const size = ARCADE_GAME_KINDS.length;
  const block = Math.floor(day / size);
  return orderForBlock(block)[((day % size) + size) % size];
}

// Highest hash wins (rendezvous hashing): a song added mid-day only changes
// the pick if the new song happens to win. Yesterday's song sits out so the
// same song does not come round two days running; yesterday is worked out the
// same way a few days deep, which is exact unless a run of unlikely ties
// lines up.
const SONG_LOOKBACK = 6;
function topSong<T extends { slug: string }>(songs: T[], date: string, skip?: string): T | null {
  let best: T | null = null;
  let bestHash = -1;
  for (const song of songs) {
    if (song.slug === skip) continue;
    const value = hash32(`challenge-song:${date}:${song.slug}`);
    if (value > bestHash) {
      best = song;
      bestHash = value;
    }
  }
  return best;
}

export interface DailyChallenge<T> {
  date: string;
  song: T;
  game: ArcadeGameKind;
}

export function dailyChallengeFor<T extends { slug: string }>(
  songs: T[],
  date: string,
  offers: (song: T, game: ArcadeGameKind) => boolean = () => true,
): DailyChallenge<T> | null {
  const song = pickSong(songs, date, offers, SONG_LOOKBACK);
  return song ? { date, song, game: challengeGameFor(date) } : null;
}

function pickSong<T extends { slug: string }>(songs: T[], date: string, offers: (song: T, game: ArcadeGameKind) => boolean, depth: number): T | null {
  const game = challengeGameFor(date);
  const eligible = songs.filter((song) => offers(song, game));
  if (eligible.length === 0) return null;
  const yesterday = utcDateKey(Date.parse(`${date}T00:00:00Z`) - DAY_MS);
  const skip = depth > 0 && eligible.length > 1 ? pickSong(songs, yesterday, offers, depth - 1)?.slug : undefined;
  return topSong(eligible, date, skip);
}

// A challenge score counts for the day it was played, or for yesterday if the
// run started before midnight UTC and ended after it.
export function isOpenChallengeDate(date: string, nowMs: number): boolean {
  if (!isDateKey(date)) return false;
  const today = utcDateKey(nowMs);
  return date === today || date === utcDateKey(nowMs - DAY_MS);
}

export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}
